import type { ReactNode } from 'react';

import { env } from '@/env';
import { useLogtoRoles } from '@/hooks/useLogtoRoles';
import { hasAnyRole } from '@/lib/roleRevalidation';
import { AccessDenied } from './AccessDenied';

interface AdminGateProps {
  children: ReactNode;
}

/**
 * Roles on Logto's PROD tenant that grant access to a gated build. Mirrors
 * what's set up in `admin-account.qlash.gg → Ruoli`: `administrator` is the
 * full-admin grant, `beta` lets beta testers into the DEV/staging build.
 */
const PRIVILEGED_LOGTO_ROLES = ['administrator', 'beta'] as const;

/**
 * Build-level access gate. When `env.adminGateEnabled === true` (DEV build,
 * `@QLASH_DEV_bot` at `dev-tma.qlash.gg`), only users carrying at least one
 * of the privileged Logto roles proceed; everyone else sees <AccessDenied />.
 *
 * Roles are read from the Logto access token's `roles` claim (injected by
 * the PROD tenant's Custom JWT script — see
 * `getLogtoRolesFromAccessToken` for the why). We do NOT read `is_admin` /
 * `beta_tester` from Laravel `/users/me` because Laravel doesn't expose
 * those fields to mini-app callers.
 *
 * The PROD build (`tma.qlash.gg`) leaves `adminGateEnabled === false`, so
 * this component renders its children unconditionally for all users.
 *
 * Mirrors the `?dev=1` bypass from <AppShell /> so the dev experience works
 * locally even without privileged Logto roles.
 *
 * Placement: this gate sits AFTER the auth gate in <AppShell /> — when
 * `bridgeAuthenticated === false`, the user is shown <Login /> before this
 * component is ever evaluated.
 *
 * 🔴 **I ruoli si RILEGGONO quando il token cambia** (2026-09-06). Prima questo gate
 * memoizzava su `bridgeAuthenticated` e basta, col commento «bridgeAuthenticated flips when
 * the access token is stored/cleared, so re-evaluating roles on its change is enough» —
 * che e' falso per il caso che conta: **`bridgeAuthenticated` non cambia quando il token
 * viene RINNOVATO**, cioe' esattamente quando arrivano i ruoli appena assegnati. Effetto
 * misurato sul gemello `RoleGate` (or45, `stg-tma`): l'utente resta fuori finche' non
 * ricarica. Qui il sintomo era piu' mite — `<AccessDenied />` invece di una schermata vuota
 * — e proprio per questo sarebbe durato di piu': una schermata che spiega non fa sospettare
 * un difetto.
 * ⚠️ Simmetrico: un token rinnovato senza i ruoli richiude il gate durante la sessione.
 * Corretto — un gate che ricordasse un privilegio revocato sarebbe peggio.
 */
export function AdminGate({ children }: AdminGateProps) {
  // `useLogtoRoles` porta gia' dentro l'autenticazione (utente non autenticato ⇒ `[]`) e la
  // rivalutazione al cambio token. Lo store e' UNO (`lib/roleRevalidation.ts`): qui non si
  // ricostruisce, si usa.
  const roles = useLogtoRoles();
  const isPrivileged = hasAnyRole(roles, PRIVILEGED_LOGTO_ROLES);

  const devBypass =
    import.meta.env.DEV && new URLSearchParams(window.location.search).has('dev');

  if (!env.adminGateEnabled || devBypass) {
    return <>{children}</>;
  }

  if (!isPrivileged) {
    return <AccessDenied />;
  }

  return <>{children}</>;
}
